import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const COLOURS = [
  '#000000',
  '#FFFFFF',
  '#EF4444',
  '#F97316',
  '#EAB308',
  '#22C55E',
  '#06B6D4',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
];

export default function FontColorControl({ value = '#000000', onChange }) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [popupStyle, setPopupStyle] = useState({
    position: 'fixed',
    top: '-9999px',
    left: '-9999px',
    zIndex: 9999,
  });

  const buttonRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  const updatePopupPosition = () => {
    const btn = buttonRef.current;
    const popup = popupRef.current;
    if (!btn || !popup) return;

    const rect = btn.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();
    const gap = 8;

    let left = rect.left + rect.width / 2 - popupRect.width / 2;
    let top = rect.top - popupRect.height - gap;

    if (left < 8) left = 8;
    if (left + popupRect.width > window.innerWidth - 8) {
      left = window.innerWidth - popupRect.width - 8;
    }

    if (top < 8) {
      top = rect.bottom + gap;
    }

    setPopupStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 9999,
    });
  };

  useLayoutEffect(() => {
    if (!mounted) return;

    const frame = requestAnimationFrame(() => {
      updatePopupPosition();
    });

    return () => cancelAnimationFrame(frame);
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;

    const handleOutside = (e) => {
      if (
        buttonRef.current?.contains(e.target) ||
        popupRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
      setMounted(false);
    };

    const handleReposition = () => updatePopupPosition();

    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [mounted]);

  const handleSelect = (colour) => {
    onChange?.(colour);
    setOpen(false);
    setMounted(false);
  };

  const popup = mounted
    ? createPortal(
        <div
          ref={popupRef}
          style={popupStyle}
          className="rounded-lg border border-neutral-200 bg-white p-2 shadow-lg"
        >
          <div className="grid grid-cols-5 gap-2">
            {COLOURS.map((colour) => {
              const isSelected = value === colour;

              return (
                <button
                  key={colour}
                  type="button"
                  onClick={() => handleSelect(colour)}
                  className={`h-6 w-6 rounded-sm border ${
                    colour === '#FFFFFF'
                      ? 'border-neutral-400'
                      : 'border-neutral-200'
                  } ${isSelected ? 'ring-2 ring-black ring-offset-1' : ''}`}
                  style={{ backgroundColor: colour }}
                  aria-label={`Select colour ${colour}`}
                  title={colour}
                />
              );
            })}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <label className="text-sm text-gray-600 whitespace-nowrap">Color</label>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          if (open) {
            setOpen(false);
            setMounted(false);
          } else {
            setOpen(true);
          }
        }}
        className="flex h-8 w-8 items-center justify-center rounded border border-neutral-300 bg-white shadow-sm"
        aria-label="Choose text colour"
        title="Choose text colour"
      >
        <span
          className="block h-5 w-5 rounded-sm border border-neutral-300"
          style={{ backgroundColor: value }}
        />
      </button>

      {popup}
    </>
  );
}
